// frontend/src/components/handbook/HandbookChapter.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  BookOpen,
} from "lucide-react";
import HandbookChecklist from "./HandbookChecklist";
import ContentTypeBadge from "./ContentTypeBadge";
import ChapterStatusBadge from "./ChapterStatusBadge";
import "./HandbookChapter.css";

const PROSE_VISIT_KEY_PREFIX = "coachHandbook.proseVisited.";

const HandbookChapter = ({
  chapter,
  prevChapter,
  nextChapter,
  onProgressChange,
}) => {
  const navigate = useNavigate();
  const [proseOpen, setProseOpen] = useState(false);

  // Decide initial prose state on chapter change.
  useEffect(() => {
    if (!chapter) return;
    const visitedKey = PROSE_VISIT_KEY_PREFIX + chapter.slug;
    const visited =
      typeof window !== "undefined" && localStorage.getItem(visitedKey) === "1";
    const hasChecklists =
      Array.isArray(chapter.checklists) && chapter.checklists.length > 0;
    const hasProse = Boolean(chapter.content_html);

    // Default rule:
    // - reference chapter or no checklists → open
    // - already visited & has checklists → collapsed
    // - first visit & has both → open (so reader sees context once)
    if (!hasProse) {
      setProseOpen(false);
    } else if (!hasChecklists || chapter.content_type === "reference") {
      setProseOpen(true);
    } else if (visited) {
      setProseOpen(false);
    } else {
      setProseOpen(true);
    }

    if (typeof window !== "undefined") {
      localStorage.setItem(visitedKey, "1");
    }
  }, [chapter]);

  // Scroll to top on chapter change unless URL has a hash
  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [chapter?.slug]);

  if (!chapter) return null;

  const goToChapter = (slug) => {
    if (!slug) return;
    navigate(`/coach-handbook/${slug}`);
  };

  const hasChecklists =
    Array.isArray(chapter.checklists) && chapter.checklists.length > 0;
  const hasProse = Boolean(chapter.content_html);

  const ProseBlock = (
    <section className="mt-2 mb-2">
      <button
        type="button"
        onClick={() => setProseOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-[#00A3E0]/5 hover:bg-[#00A3E0]/10 border border-[#00A3E0]/20 rounded-lg transition-colors"
        aria-expanded={proseOpen}
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#00A3E0]" />
          <span className="text-sm font-semibold text-[#002855]">
            Hintergrund
          </span>
          <span className="text-xs text-gray-500 hidden sm:inline">
            (einmalig lesen)
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-[#00A3E0] transition-transform ${
            proseOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {proseOpen && (
        <div
          className="handbook-prose mt-4"
          dangerouslySetInnerHTML={{ __html: chapter.content_html || "" }}
        />
      )}
    </section>
  );

  const ChecklistsBlock = (
    <HandbookChecklist
      chapterSlug={chapter.slug}
      checklists={chapter.checklists}
      onProgressChange={onProgressChange}
    />
  );

  return (
    <article className="max-w-3xl">
      {chapter.cover_image && (
        <div className="mb-6 rounded-lg overflow-hidden bg-gray-100 aspect-video">
          <img
            src={chapter.cover_image}
            alt={chapter.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
      )}

      <header className="mb-6">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {chapter.section_title && (
            <span className="text-xs uppercase tracking-wider text-[#84BD00] font-semibold">
              {chapter.section_title}
            </span>
          )}
          {chapter.content_type && (
            <ContentTypeBadge type={chapter.content_type} />
          )}
          <ChapterStatusBadge status={chapter.status} />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#002855] leading-tight mb-2">
          {chapter.title}
        </h1>
        {chapter.subtitle && (
          <p className="text-base md:text-lg text-gray-600">
            {chapter.subtitle}
          </p>
        )}
        {chapter.status === "draft" && (
          <div className="mt-3 px-3 py-2 bg-gray-50 border-l-4 border-gray-400 text-sm text-gray-600 rounded-r">
            ⚠️ Dieses Kapitel ist als <strong>Entwurf</strong> markiert — Inhalt ist noch nicht freigegeben.
          </div>
        )}
        {chapter.status === "wip" && (
          <div className="mt-3 px-3 py-2 bg-amber-50 border-l-4 border-amber-400 text-sm text-amber-900 rounded-r">
            🛠 Dieses Kapitel ist <strong>in Arbeit</strong> — Inhalt kann sich noch ändern.
          </div>
        )}
      </header>

      {/* Order depends on content type */}
      {chapter.content_type === "reference" ? (
        <>
          {hasProse && (
            <div
              className="handbook-prose"
              dangerouslySetInnerHTML={{ __html: chapter.content_html || "" }}
            />
          )}
          {hasChecklists && ChecklistsBlock}
        </>
      ) : (
        <>
          {hasChecklists && ChecklistsBlock}
          {hasProse && ProseBlock}
        </>
      )}

      <nav className="mt-10 pt-6 border-t border-gray-200 flex items-stretch justify-between gap-2">
        <button
          type="button"
          onClick={() => goToChapter(prevChapter?.slug)}
          disabled={!prevChapter}
          className="flex-1 flex items-center justify-start gap-2 px-3 py-3 rounded-lg border border-gray-200 hover:border-[#00A3E0] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed text-left transition-colors"
        >
          <ChevronLeft className="w-5 h-5 flex-shrink-0 text-[#00A3E0]" />
          <div className="min-w-0">
            <div className="text-xs text-gray-500">Vorheriges</div>
            <div className="text-sm font-medium text-[#002855] truncate">
              {prevChapter?.title || "—"}
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => goToChapter(nextChapter?.slug)}
          disabled={!nextChapter}
          className="flex-1 flex items-center justify-end gap-2 px-3 py-3 rounded-lg border border-gray-200 hover:border-[#00A3E0] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed text-right transition-colors"
        >
          <div className="min-w-0">
            <div className="text-xs text-gray-500">Nächstes</div>
            <div className="text-sm font-medium text-[#002855] truncate">
              {nextChapter?.title || "—"}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 flex-shrink-0 text-[#00A3E0]" />
        </button>
      </nav>
    </article>
  );
};

export default HandbookChapter;
